import { useMemo, useRef, useState } from 'react';

import { ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import {
  useCreateFertWorkSessionMutation,
  useDeleteSessionMutation,
  useGetFertWorkSessionsQuery,
} from '@/app/services/fertWork';
import { WithLocalStorage } from '@/components';
import TechniciansDropdown from '@/components/common/technicians-dropdown';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getDateString } from '@/pages/fert-work/utils/dateHelpers';
import {
  STATUS_CODES,
  statusCodeToLabel,
} from '@/pages/fert-work/utils/statusHelpers';
import { hasPermission, MODULES } from '@/services/auth';
import { TechnicianItem } from '@/services/lookups/types';
import { useSites } from '@/services/sites';
import { useContextMenu } from '@/utils/context-menu';

import '../../fertWorkTypography.css';

export type FertWorkHistoryProps = {
  className?: string;
};

type SessionHistoryRow = {
  id: string;
  date: string;
  aspirationDate: string;
  siteName: string;
  siteId: string;
  status: number;
  createdBy: string;
  runCount: number;
  sessionId: string;
};

const ActionsRenderer = (props: ICellRendererParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { onDelete, hasWritePermission } = props.context;

  const getActionLabel = (status: number) => {
    switch (status) {
      case STATUS_CODES.IN_PROGRESS:
        return t('fertWork.continue', 'Continue');
      case STATUS_CODES.VALIDATING:
        return t('fertWork.edit', 'Edit');
      case STATUS_CODES.COMPLETE:
        return t('fertWork.view', 'View');
      case STATUS_CODES.ERROR:
        return t('fertWork.review', 'Review');
      default:
        return t('fertWork.open', 'Open');
    }
  };

  const handleOpenSession = () => {
    navigate(
      `/fert-work/session?sessionId=${props.data.id}&date=${props.data.date}&siteId=${props.data.siteId}`,
    );
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
        onClick={handleOpenSession}
      >
        {getActionLabel(props.data.status)}
      </button>
      {hasWritePermission && (
        <>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-red-600 transition-colors hover:text-red-800 hover:underline"
            onClick={() => onDelete(props.data.id)}
          >
            {t('fertWork.delete', 'Delete')}
          </button>
        </>
      )}
    </div>
  );
};

export const FertWorkHistory = ({ className = '' }: FertWorkHistoryProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const { me } = useAppSelector((state) => state.authentication);

  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [aspirationDate, setAspirationDate] = useState<Date | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const [filterDate, setFilterDate] = useState<Date | null>(null);
  const [filterSiteId, setFilterSiteId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<number | null>(null);
  const [filterTech, setFilterTech] = useState<TechnicianItem | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: fertilizationSites } = useSites({ filter: 'fertilization' });

  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      startRow: (currentPage - 1) * pageSize + 1,
      endRow: currentPage * pageSize,
    };
    if (filterSiteId) params.siteId = filterSiteId;
    if (filterDate) params.aspirationDate = format(filterDate, 'yyyy-MM-dd');
    if (filterStatus !== null) params.status = String(filterStatus);
    if (filterTech?.id) params.createdById = filterTech.id;
    return params;
  }, [
    currentPage,
    pageSize,
    filterSiteId,
    filterDate,
    filterStatus,
    filterTech,
  ]);

  const { data: apiSessions = [], isLoading: isLoadingSessions } =
    useGetFertWorkSessionsQuery(queryParams);
  const [createSession, { isLoading: isCreating }] =
    useCreateFertWorkSessionMutation();
  const [deleteSession] = useDeleteSessionMutation();

  const hasWritePermission = hasPermission(me, MODULES.FERT_WORK_WRITE);

  const statusOptions = useMemo(
    () => [
      {
        label: t('fertWork.inProgress', 'In Progress'),
        value: STATUS_CODES.IN_PROGRESS,
      },
      { label: t('fertWork.draft', 'Draft'), value: STATUS_CODES.VALIDATING },
      {
        label: t('fertWork.complete', 'Complete'),
        value: STATUS_CODES.COMPLETE,
      },
      { label: t('fertWork.error', 'Error'), value: STATUS_CODES.ERROR },
    ],
    [t],
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'date',
        headerName: t('fertWork.sessionDate', 'Session Date'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'status',
        headerName: t('fertWork.status', 'Status'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: any) => statusCodeToLabel(params.value),
        cellStyle: (params: any) => {
          const statusLabel = statusCodeToLabel(params.value);
          switch (statusLabel) {
            case 'In Progress':
              return {
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
              };
            case 'Draft':
              return {
                backgroundColor: '#eab308',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
              };
            case 'Complete':
              return {
                backgroundColor: '#22c55e',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
              };
            case 'Error':
              return {
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
              };
            default:
              return {};
          }
        },
      },
      {
        field: 'siteName',
        headerName: t('fertWork.site', 'Site'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'createdBy',
        headerName: t('fertWork.createdBy', 'Created By'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'runCount',
        headerName: t('fertWork.runCount', 'Run Count'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'sessionId',
        headerName: t('fertWork.sessionId', 'Session ID'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: any) => {
          if (!params.value) return '';
          const guid = params.value.toString();
          return guid.slice(0, 8).toUpperCase();
        },
        tooltipField: 'sessionId',
      },
      {
        field: 'aspirationDate',
        headerName: t('fertWork.aspirationDateHeader', 'Aspiration Date'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'actions',
        headerName: t('fertWork.actions', 'Actions'),
        cellRenderer: ActionsRenderer,
        sortable: false,
        filter: false,
        flex: 1,
      },
    ],
    [t],
  );

  const handleDeleteSession = async (sessionId: string) => {
    if (
      !confirm(
        t(
          'fertWork.deleteSessionConfirm',
          'Are you sure you want to delete this session? This action cannot be undone.',
        ),
      )
    ) {
      return;
    }

    try {
      await deleteSession(sessionId).unwrap();
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert(
        t(
          'fertWork.toasts.deleteFailedDetail',
          'Failed to delete session. Please try again.',
        ),
      );
    }
  };

  const rowData = useMemo(() => {
    let filteredSessions = apiSessions.map((session) => ({
      id: session.id,
      sessionId: session.sessionId,
      date: format(new Date(session.createdAt), 'yyyy-MM-dd'),
      aspirationDate: getDateString(session.aspirationDate),
      siteName: session.siteName,
      siteId: session.siteId,
      status: session.status,
      createdBy: session.createdByName,
      runCount: session.runCount,
    }));

    if (filterDate) {
      const filterDateStr = format(filterDate, 'yyyy-MM-dd');
      filteredSessions = filteredSessions.filter(
        (session) => session.date === filterDateStr,
      );
    }

    if (filterSiteId) {
      filteredSessions = filteredSessions.filter(
        (session) => session.siteId === filterSiteId,
      );
    }

    if (filterStatus !== null) {
      filteredSessions = filteredSessions.filter(
        (session) => session.status === filterStatus,
      );
    }

    if (filterTech?.id) {
      filteredSessions = filteredSessions.filter((session) => {
        const apiSession = apiSessions.find((s) => s.id === session.id);
        return apiSession?.createdById === filterTech.id;
      });
    }

    return filteredSessions.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [apiSessions, filterDate, filterSiteId, filterStatus, filterTech]);

  const handleBackToSessions = () => {
    navigate('/fert-work');
  };

  const handleClearFilters = () => {
    setFilterDate(null);
    setFilterSiteId(null);
    setFilterStatus(null);
    setFilterTech(null);
  };

  const handleNewSession = () => {
    setShowNewSessionDialog(true);
  };

  const handleStartSession = async () => {
    if (!aspirationDate || !selectedSiteId) return;

    try {
      const formattedDate = format(aspirationDate, 'yyyy-MM-dd');

      const result = await createSession({
        aspirationDate: formattedDate,
        siteId: selectedSiteId,
      }).unwrap();

      setShowNewSessionDialog(false);
      setAspirationDate(null);
      setSelectedSiteId(null);

      navigate(
        `/fert-work/session?sessionId=${result.id}&date=${formattedDate}&siteId=${selectedSiteId}`,
      );
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const handleCancelNewSession = () => {
    setShowNewSessionDialog(false);
    setAspirationDate(null);
    setSelectedSiteId(null);
  };

  const handleRowDoubleClick = (event: any) => {
    const session = event.data;
    navigate(
      `/fert-work/session?sessionId=${session.id}&date=${session.date}&siteId=${session.siteId}`,
    );
  };

  return (
    <div
      className={`fert-work-history relative flex flex-auto flex-col ${className}`}
    >
      <div className="flex flex-col gap-2 p-2">
        <h2 className="text-base font-medium">
          {t('fertWork.sessionHistory', 'Session History')}
        </h2>

        <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-wrap gap-1">
            {hasWritePermission && (
              <Button onClick={handleNewSession}>
                <i className="pi pi-plus" data-icon="inline-start" />
                {t('fertWork.newSession', 'New Session')}
              </Button>
            )}
            <Button variant="secondary" onClick={handleBackToSessions}>
              <i className="pi pi-arrow-left" data-icon="inline-start" />
              {t('fertWork.backToSessions', 'Back to Sessions')}
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <Select
              items={(fertilizationSites ?? []).map((site) => ({
                label: site.name,
                value: site.id,
              }))}
              value={filterSiteId}
              onChange={setFilterSiteId}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue
                  placeholder={t('fertWork.filterBySite', 'Filter by site')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>{t('common.all', 'All')}</SelectItem>
                {(fertilizationSites ?? []).map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DatePicker
              value={filterDate ?? undefined}
              onChange={(date) => setFilterDate(date ?? null)}
              placeholder={t('fertWork.filterByDate', 'Filter by date')}
              className="h-8 w-full md:w-48"
            />

            <Select
              items={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue
                  placeholder={t('fertWork.filterByStatus', 'Filter by status')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>{t('common.all', 'All')}</SelectItem>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="w-full md:w-48">
              <TechniciansDropdown
                value={filterTech}
                onChange={setFilterTech}
                placeholder={t('fertWork.filterByTech', 'Filter by tech')}
                paramKey="techId"
              />
            </div>

            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={handleClearFilters}
                  >
                    <i className="pi pi-filter-slash" />
                    <span className="sr-only">
                      {t('fertWork.clearFilters', 'Clear Filters')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="top">
                {t('fertWork.clearFilters', 'Clear Filters')}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="h-[600px]">
          <WithLocalStorage storageKey="fert-work-history-grid">
            <AgGridReact
              gridOptions={defaultGridOptions}
              ref={gridRef}
              className="ag-theme-transova"
              defaultColDef={defaultColDef}
              rowData={rowData}
              columnDefs={columnDefs}
              pagination={true}
              paginationPageSize={pageSize}
              paginationPageSizeSelector={[20, 50, 100, 250, 500, 1000]}
              overlayNoRowsTemplate={t(
                'fertWork.noSessionsFound',
                'No sessions found',
              )}
              getRowId={(params) => params.data.id}
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={handleRowDoubleClick}
              onPaginationChanged={(event) => {
                const newPageSize = event.api.paginationGetPageSize();
                const newPage = event.api.paginationGetCurrentPage() + 1;
                if (newPageSize !== pageSize) {
                  setPageSize(newPageSize);
                  setCurrentPage(1);
                } else if (newPage !== currentPage) {
                  setCurrentPage(newPage);
                }
              }}
              context={{ onDelete: handleDeleteSession, hasWritePermission }}
            />
          </WithLocalStorage>
        </div>
      </div>

      <Dialog
        open={showNewSessionDialog}
        onOpenChange={(open) => {
          if (!open) handleCancelNewSession();
        }}
      >
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('fertWork.startNewSession', 'Start New Session')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="aspiration-date">
                {t('fertWork.aspirationDate', 'Aspiration Date')}
              </label>
              <DatePicker
                id="aspiration-date"
                value={aspirationDate ?? undefined}
                onChange={(date) => setAspirationDate(date ?? null)}
                placeholder={t('fertWork.selectDate', 'Select date')}
                className="h-8 w-full"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="fert-site">
                {t('fertWork.fertSite', 'Fert Site')}
              </label>
              <Select
                items={(fertilizationSites ?? []).map((site) => ({
                  label: site.name,
                  value: site.id,
                }))}
                value={selectedSiteId}
                onChange={setSelectedSiteId}
              >
                <SelectTrigger id="fert-site" className="w-full">
                  <SelectValue
                    placeholder={t(
                      'fertWork.selectFertSite',
                      'Select fert site',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(fertilizationSites ?? []).map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={handleCancelNewSession}>
              {t('fertWork.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleStartSession}
              disabled={!aspirationDate || !selectedSiteId || isCreating}
            >
              {isCreating && <i className="pi pi-spin pi-spinner" />}
              {t('fertWork.start', 'Start')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
